
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, AlertTriangle, Heart, Activity, Shield, Target } from 'lucide-react';
import { useTrainingLoadAnalysis } from '@/hooks/useTrainingLoadAnalysis';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

export const EnhancedTrainingLoadDashboard: React.FC = () => {
  const { data: trainingData, isLoading } = useTrainingLoadAnalysis(42); // 6 weeks of data

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const { trainingLoadMetrics, currentStatus, heartRateMetrics } = trainingData || {};
  
  // Calculate acute chronic ratio status
  const getACRStatus = (ratio: number) => {
    if (ratio < 0.8) return { status: 'Detraining', color: 'blue', risk: 'low' };
    if (ratio < 1.0) return { status: 'Maintaining', color: 'green', risk: 'low' };
    if (ratio < 1.3) return { status: 'Productive', color: 'green', risk: 'low' };
    if (ratio < 1.5) return { status: 'Overreaching', color: 'orange', risk: 'medium' };
    return { status: 'Overtraining Risk', color: 'red', risk: 'high' };
  };

  const acrStatus = getACRStatus(currentStatus?.acuteChronic || 1.0);

  // Prepare chart data
  const chartData = trainingLoadMetrics?.slice(0, 21).reverse().map((metric, index) => ({
    date: new Date(metric.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    acute: metric.rolling7dTrimp / 7,
    chronic: metric.rolling28dTrimp / 28,
    tsb: (metric.rolling28dTrimp / 28) - (metric.rolling7dTrimp / 7),
    dailyLoad: metric.dailyTrimpScore
  })) || [];

  return (
    <div className="space-y-6">
      {/* Alert for high risk */}
      {acrStatus.risk === 'high' && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>High Injury Risk Detected:</strong> Your acute:chronic workload ratio is {currentStatus?.acuteChronic.toFixed(2)}. 
            Consider reducing training intensity and adding recovery days.
          </AlertDescription>
        </Alert>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Acute:Chronic Ratio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{currentStatus?.acuteChronic.toFixed(2)}</span>
              <Badge 
                variant="secondary" 
                className={`bg-${acrStatus.color}-100 text-${acrStatus.color}-800`}
              >
                {acrStatus.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              7-day vs 28-day average load
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Heart className="h-4 w-4 text-red-600" />
              Recovery Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{Math.round(currentStatus?.recoveryScore || 100)}%</span>
              <Badge 
                variant="secondary" 
                className={`${currentStatus?.recoveryScore >= 80 ? 'bg-green-100 text-green-800' : 
                            currentStatus?.recoveryScore >= 60 ? 'bg-orange-100 text-orange-800' : 
                            'bg-red-100 text-red-800'}`}
              >
                {currentStatus?.recoveryScore >= 80 ? 'Good' : 
                 currentStatus?.recoveryScore >= 60 ? 'Fair' : 'Poor'}
              </Badge>
            </div>
            <Progress 
              value={currentStatus?.recoveryScore || 100} 
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-600" />
              Avg TRIMP Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{Math.round(heartRateMetrics?.avgTrimpScore || 0)}</span>
              <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                {heartRateMetrics?.hrDataCount || 0} activities
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Training impulse per session
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              Injury Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold capitalize">{currentStatus?.overreachRisk}</span>
              <Badge 
                variant="secondary" 
                className={`${currentStatus?.overreachRisk === 'low' ? 'bg-green-100 text-green-800' : 
                            currentStatus?.overreachRisk === 'medium' ? 'bg-orange-100 text-orange-800' : 
                            'bg-red-100 text-red-800'}`}
              >
                {currentStatus?.overreachRisk === 'low' ? 'Low Risk' : 
                 currentStatus?.overreachRisk === 'medium' ? 'Medium Risk' : 'High Risk'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Based on training patterns
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Training Stress Balance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Training Stress Balance (21-day view)
          </CardTitle>
          <CardDescription>
            Acute (7-day) vs Chronic (28-day) training load with Training Stress Balance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    value.toFixed(1),
                    name === 'acute' ? 'Acute Load (7d avg)' :
                    name === 'chronic' ? 'Chronic Load (28d avg)' :
                    name === 'tsb' ? 'Training Stress Balance' : name
                  ]}
                />
                <Area 
                  type="monotone" 
                  dataKey="chronic" 
                  stackId="1" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.3}
                />
                <Area 
                  type="monotone" 
                  dataKey="acute" 
                  stackId="2" 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  fillOpacity={0.6}
                />
                <Line 
                  type="monotone" 
                  dataKey="tsb" 
                  stroke="#ff7300" 
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Training Recommendations</CardTitle>
          <CardDescription>
            AI-powered suggestions based on your training load analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {acrStatus.risk === 'high' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-medium text-red-800 mb-1">Immediate Action Required</h4>
                <p className="text-sm text-red-700">
                  Consider taking 2-3 easy days or a complete rest day. Your training load spike puts you at high injury risk.
                </p>
              </div>
            )}
            
            {currentStatus?.recoveryScore < 60 && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-1">Recovery Focus Needed</h4>
                <p className="text-sm text-orange-700">
                  Prioritize sleep, nutrition, and easy-paced runs. Consider adding a recovery week to your schedule.
                </p>
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-1">Next Week Strategy</h4>
              <p className="text-sm text-blue-700">
                {(currentStatus?.acuteChronic || 1.0) < 1.0 
                  ? "You can safely increase training volume by 10-15% while monitoring recovery."
                  : "Maintain current training volume and focus on consistency over intensity."
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
